/**
 * Copyright (c) 2025 Répét Contributors
 * Licensed under the MIT License
 * See LICENSE file in the project root for full license text
 */

import Dexie, { Table } from 'dexie'
import { v4 as uuidv4 } from 'uuid'
import { Note, NotesPreferences, NoteDisplayState } from '../models/note'

/**
 * Base de données IndexedDB pour les notes
 * Utilise Dexie pour simplifier les opérations
 */
class NotesDatabase extends Dexie {
  notes!: Table<Note, string>
  preferences!: Table<NotesPreferences, string>

  constructor() {
    super('RepetNotesDB')

    // Version 1 du schéma (legacy)
    this.version(1).stores({
      notes: 'id, playId, [playId+attachedToType+attachedToIndex]',
      preferences: 'id',
    })

    // Version 2 du schéma - Migration playId → versionId
    this.version(2)
      .stores({
        notes: 'id, versionId, [versionId+attachedToType+attachedToIndex]',
        preferences: 'id',
      })
      .upgrade(async (_trans) => {
        // Migration simple : playId devient versionId
        // Les anciennes notes de V1 resteront attachées à l'ancien playId
        // qui correspond maintenant à un textId. Les nouvelles notes
        // seront créées avec versionId.
        // Migration v1→v2: playId → versionId (no data transformation needed)
      })

    // Version 3 du schéma - Ajout userId et attachedToElementStableId
    this.version(3)
      .stores({
        notes:
          'id, versionId, userId, attachedToElementStableId, [versionId+userId], [versionId+attachedToType+attachedToIndex]',
        preferences: 'id',
      })
      .upgrade(async (_trans) => {
        // Les notes existantes devront avoir userId et attachedToElementStableId ajoutés
        // lors de leur première utilisation
      })
  }
}

const db = new NotesDatabase()

/**
 * Service de gestion du stockage des notes
 */
export class NotesStorage {
  /**
   * Génère un nouvel ID unique pour une note
   */
  private static generateId(): string {
    return uuidv4()
  }

  /**
   * Crée une nouvelle note
   */
  static async createNote(
    versionId: string,
    userId: string,
    attachedToType: Note['attachedToType'],
    attachedToIndex: number,
    attachedToElementStableId: string,
    content: string = ''
  ): Promise<Note> {
    const now = new Date()
    const note: Note = {
      id: this.generateId(),
      versionId,
      userId,
      attachedToType,
      attachedToIndex,
      attachedToElementStableId,
      content,
      displayState: NoteDisplayState.MAXIMIZED,
      createdAt: now,
      updatedAt: now,
    }

    await db.notes.add(note)
    return note
  }

  /**
   * Récupère une note par son ID
   */
  static async getNote(id: string): Promise<Note | undefined> {
    return await db.notes.get(id)
  }

  /**
   * Récupère toutes les notes d'une version
   */
  static async getNotesByVersionId(versionId: string): Promise<Note[]> {
    return await db.notes.where('versionId').equals(versionId).toArray()
  }

  /**
   * Récupère la note attachée à un élément spécifique (par stableId)
   */
  static async getNoteByAttachment(
    versionId: string,
    userId: string,
    attachedToElementStableId: string
  ): Promise<Note | undefined> {
    return await db.notes.where({ versionId, userId, attachedToElementStableId }).first()
  }

  /**
   * Récupère les notes d'un utilisateur pour une version
   */
  static async getNotesForVersionAndUser(versionId: string, userId: string): Promise<Note[]> {
    return await db.notes.where('[versionId+userId]').equals([versionId, userId]).toArray()
  }

  /**
   * Compte les notes d'un utilisateur pour une version
   */
  static async countNotesForVersionAndUser(versionId: string, userId: string): Promise<number> {
    return await db.notes.where('[versionId+userId]').equals([versionId, userId]).count()
  }

  /**
   * Création en batch de notes
   */
  static async bulkCreateNotes(notes: Note[]): Promise<void> {
    await db.notes.bulkAdd(notes)
  }

  /**
   * Met à jour le contenu d'une note
   */
  static async updateNoteContent(id: string, content: string): Promise<void> {
    await db.notes.update(id, {
      content,
      updatedAt: new Date(),
    })
  }

  /**
   * Met à jour l'état d'affichage d'une note
   */
  static async updateNoteDisplayState(id: string, displayState: NoteDisplayState): Promise<void> {
    await db.notes.update(id, {
      displayState,
      updatedAt: new Date(),
    })
  }

  /**
   * Supprime une note
   */
  static async deleteNote(id: string): Promise<void> {
    await db.notes.delete(id)
  }

  /**
   * Supprime toutes les notes d'une version
   */
  static async deleteNotesByVersionId(versionId: string): Promise<void> {
    await db.notes.where('versionId').equals(versionId).delete()
  }

  /**
   * Minimise ou maximise toutes les notes d'une version
   */
  static async setAllNotesDisplayState(
    versionId: string,
    displayState: NoteDisplayState
  ): Promise<void> {
    const notes = await this.getNotesByVersionId(versionId)
    const now = new Date()

    await db.transaction('rw', db.notes, async () => {
      for (const note of notes) {
        await db.notes.update(note.id, {
          displayState,
          updatedAt: now,
        })
      }
    })
  }

  /**
   * Récupère les préférences globales
   */
  static async getPreferences(): Promise<NotesPreferences> {
    const prefs = await db.preferences.get('global')

    if (!prefs) {
      // Créer préférences par défaut
      const defaultPrefs: NotesPreferences = {
        id: 'global',
        defaultDisplayState: NoteDisplayState.MAXIMIZED,
        updatedAt: new Date(),
      }
      await db.preferences.add(defaultPrefs)
      return defaultPrefs
    }

    return prefs
  }

  /**
   * Met à jour les préférences globales
   */
  static async updatePreferences(updates: Partial<Omit<NotesPreferences, 'id'>>): Promise<void> {
    await db.preferences.update('global', {
      ...updates,
      updatedAt: new Date(),
    })
  }

  /**
   * Nettoie toutes les données (pour tests/debug)
   */
  static async clearAll(): Promise<void> {
    await db.notes.clear()
    await db.preferences.clear()
  }
}
